import { useEffect } from 'react'
import { useMutation } from 'react-query'
import { useDispatch, useSelector } from 'react-redux'
import {
  useChuckClientContext,
  useAPI,
  useFileUpload as useS3FileUpload
} from '@gopeerproject/chuck'

import { ClassroomManager, ID } from '@helpers'
import { Files as FS, ActionTypes } from '@constants'
import { useClassroomDispatch } from '..'

export const useFileCreate = () => {
  const { post } = useAPI()
  const { client } = useChuckClientContext()
  const dispatch = useDispatch()

  return useMutation(
    (body) => {
      dispatch({
        type: ActionTypes.CREATE_FILE.PENDING
      })

      return post({ url: '/files/dynamic', data: body }).then((res) => res.data)
    },
    {
      onSuccess: (file) => {
        dispatch({
          type: ActionTypes.CREATE_FILE.SUCCESS,
          payload: file
        })
      },
      onError: (err) => {
        dispatch({
          type: ActionTypes.CREATE_FILE.SUCCESS
        })
        client.captureException(err)
      }
    }
  )
}

export const useFileUpdate = () => {
  const { patch } = useAPI()
  const { client } = useChuckClientContext()
  const dispatch = useDispatch()

  return useMutation(
    ({ id, data }) => {
      dispatch({ type: ActionTypes.UPDATE_FILE.PENDING })
      return patch({ url: `/files/${id}`, data }).then((res) => res.data)
    },
    {
      onSuccess: (file) => {
        dispatch({
          type: ActionTypes.UPDATE_FILE.SUCCESS,
          payload: file
        })

        if (['sketch', 'note', 'code'].includes(file.type))
          ClassroomManager.updateFile(file)
      },
      onError: (err, { id }) => {
        dispatch({
          type: ActionTypes.UPDATE_FILE.ERROR,
          payload: { id }
        })
        client.captureException(err)
      }
    }
  )
}

export const useFileDelete = () => {
  const { del } = useAPI()
  const { client } = useChuckClientContext()
  const dispatch = useDispatch()

  return useMutation(
    ({ id, data }) => {
      dispatch({ type: ActionTypes.DELETE_FILE.PENDING })
      return del({ url: `/files/${id}`, data }).then((res) => res.data)
    },
    {
      onSuccess: (_, { id }) => {
        dispatch({
          type: ActionTypes.DELETE_FILE.SUCCESS,
          payload: { id }
        })
      },
      onError: (err, { id }) => {
        dispatch({
          type: ActionTypes.DELETE_FILE.ERROR,
          payload: { id }
        })
        client.captureException(err)
      }
    }
  )
}

export const useFileUpload = ({
  participants,
  onOversize = () => {},
  defaultBody
}) => {
  const uploadFile = useS3FileUpload()
  const dispatch = useDispatch()

  const upload = (files) => {
    const oversize = files.filter((v) => v.size > FS.MAX_FILE_SIZE)
    const queue = files.filter((v) => v.size <= FS.MAX_FILE_SIZE)

    if (oversize.length) onOversize(oversize)
    if (!queue.length) return

    return Promise.all(
      queue.map((file) => {
        const localId = ID.create()
        const body = {
          type: file.type,
          name: file.name,
          participants,
          ...defaultBody
        }

        dispatch({
          type: ActionTypes.UPLOAD_FILE.PENDING,
          payload: {
            localId,
            ...body
          }
        })
        uploadFile(file, body, {
          onUploadProgress: ({ progress }) => {
            dispatch({
              type: ActionTypes.UPLOAD_FILE.PENDING,
              payload: { progress, localId: localId }
            })
          }
        })
          .then((file) =>
            dispatch({
              type: ActionTypes.UPLOAD_FILE.SUCCESS,
              payload: {
                localId,
                ...file
              }
            })
          )
          .catch(() =>
            dispatch({
              type: ActionTypes.UPLOAD_FILE.ERROR,
              payload: { localId }
            })
          )
      })
    )
  }

  return upload
}

export const useFileLoader = () => {
  const { get } = useAPI()
  const { client } = useChuckClientContext()
  const dispatch = useDispatch()

  return useMutation(
    ({ id }) => {
      dispatch({ type: ActionTypes.LOAD_FILE.PENDING, payload: { id } })
      return get({ url: `/files/${id}` }).then((res) => res.data)
    },
    {
      onSuccess: (file) => {
        dispatch({
          type: ActionTypes.LOAD_FILE.SUCCESS,
          payload: file
        })
      },
      onError: (err, { id }) => {
        dispatch({
          type: ActionTypes.LOAD_FILE.ERROR,
          payload: { id }
        })
        client.captureException(err)
      }
    }
  )
}

export const useFilesLoader = () => {
  const { get } = useAPI()
  const { client } = useChuckClientContext()
  const dispatch = useDispatch()
  const classroomDispatch = useClassroomDispatch()

  const status = useSelector((state) => state.files.status)

  useEffect(() => {
    dispatch({
      type: ActionTypes.LOAD_FILES.PENDING
    })

    get({ url: '/files' })
      .then((res) => res.data)
      .then((data) => {
        dispatch({
          type: ActionTypes.LOAD_FILES.SUCCESS,
          payload: data
        })
        classroomDispatch({
          type: 'data_fetched',
          payload: 'files'
        })
      })
      .catch((err) => {
        dispatch({
          type: ActionTypes.LOAD_FILES.ERROR
        })
        client.captureException(err)
      })
  }, [get, dispatch, client, classroomDispatch])

  return status
}
